import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  DialogContentText,
  
  
} from '@mui/material';
import {
  Add,
  Visibility,
  
  
  Delete,
  LocalHospital,
  Warning,
  TrendingUp,
  TrendingDown,
  Pets,
  Update,
  Assessment,
  HealthAndSafety,
  AttachMoney,
  Receipt
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ViewBatch from './ViewBatch';
import { useData } from '../contexts/DataContext';
import { formatCurrency, formatCurrencyInput } from '../utils/currency';

const ChickenBatchManagement = () => {
  const { user } = useAuth();
  const { batches, updateBatch, addTreatment, deleteBatch } = useData();
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [mortalityDialogOpen, setMortalityDialogOpen] = useState(false);
  const [updateCountDialogOpen, setUpdateCountDialogOpen] = useState(false);
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [treatmentDialogOpen, setTreatmentDialogOpen] = useState(false);
  const [expenditureDialogOpen, setExpenditureDialogOpen] = useState(false);
  const [viewBatchOpen, setViewBatchOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Form states
  const [mortalityCount, setMortalityCount] = useState('');
  const [mortalityReason, setMortalityReason] = useState('');
  const [newCount, setNewCount] = useState('');
  const [healthStatus, setHealthStatus] = useState('');
  const [healthNotes, setHealthNotes] = useState('');
  const [treatmentType, setTreatmentType] = useState('');
  const [treatmentNotes, setTreatmentNotes] = useState('');
  const [treatmentDate, setTreatmentDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Expenditure states
  const [expenditures, setExpenditures] = useState([
    { id: 1, category: 'Food', type: '', quantity: '', unit_price: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] }
  ]);

  const handleViewBatch = (batch) => {
    setSelectedBatch(batch);
    setViewBatchOpen(true);
  };


  const handleDeleteBatch = (batch) => {
    setSelectedBatch(batch);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteBatch(selectedBatch.id);
      setDeleteConfirmOpen(false);
      setSelectedBatch(null);
    } catch (error) {
      console.error("Failed to delete batch:", error);
      alert("Failed to delete batch. Please try again.");
    }
  };
  const getHealthColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'excellent': return 'success';
      case 'good': return 'success';
      case 'fair': return 'warning';
      case 'poor': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'sold': return 'info';
      case 'deceased': return 'error';
      case 'quarantined': return 'warning';
      default: return 'default';
    }
  };

  const calculateMortalityRate = (batch) => {
    const initialCount = parseFloat(batch.initial_count) || parseFloat(batch.count) || 0;
    const currentCount = parseFloat(batch.current_count) || parseFloat(batch.count) || 0;
    
    if (initialCount === 0) return 0;
    
    const mortality = initialCount - currentCount;
    return mortality >= 0 ? ((mortality / initialCount) * 100).toFixed(1) : 0;
  };

  // Calculate statistics with proper error handling
  const calculateStats = () => {
    if (!batches || batches.length === 0) {
      return {
        totalBatches: 0,
        activeBatches: 0,
        totalBirds: 0,
        avgMortalityRate: 0
      };
    }

    const totalBatches = batches.length;
    
    // Fixed case-sensitive status comparison
    const activeBatches = batches.filter(batch => 
      batch.status && batch.status.toLowerCase() === 'active'
    ).length;
    
    // Fixed total birds calculation with proper number handling
    const totalBirds = batches.reduce((sum, batch) => {
      const count = parseFloat(batch.current_count) || parseFloat(batch.count) || 0;
      return sum + count;
    }, 0);
    
    // Calculate average mortality rate
    const mortalityRates = batches.map(batch => parseFloat(calculateMortalityRate(batch)));
    const avgMortalityRate = mortalityRates.length > 0 
      ? (mortalityRates.reduce((sum, rate) => sum + rate, 0) / mortalityRates.length).toFixed(1)
      : 0;

    return {
      totalBatches,
      activeBatches,
      totalBirds,
      avgMortalityRate
    };
  };

  const stats = calculateStats();

  const handleRecordMortality = async () => {
    if (!selectedBatch || !mortalityCount) return;

    try {
      const currentCount = parseFloat(selectedBatch.current_count) || parseFloat(selectedBatch.count) || 0;
      const mortalityNum = parseInt(mortalityCount);
      
      const updatedBatch = {
        ...selectedBatch,
        current_count: Math.max(0, currentCount - mortalityNum),
        mortality_records: [
          ...(selectedBatch.mortality_records || []),
          {
            date: new Date().toISOString(),
            count: mortalityNum,
            reason: mortalityReason,
            recorded_by: 'current_user'
          }
        ]
      };
      
      await updateBatch(updatedBatch);
      setMortalityDialogOpen(false);
      setMortalityCount('');
      setMortalityReason('');
      setSelectedBatch(null);
    } catch (error) {
      console.error('Failed to record mortality:', error);
    }
  };

  const handleUpdateCount = async () => {
    if (!selectedBatch || !newCount) return;

    try {
      const updatedBatch = {
        ...selectedBatch,
        current_count: parseInt(newCount)
      };
      
      await updateBatch(updatedBatch);
      setUpdateCountDialogOpen(false);
      setNewCount('');
      setSelectedBatch(null);
    } catch (error) {
      console.error('Failed to update count:', error);
    }
  };

  const handleUpdateHealth = async () => {
    if (!selectedBatch || !healthStatus) return;

    try {
      const updatedBatch = {
        ...selectedBatch,
        health_status: healthStatus,
        health_records: [
          ...(selectedBatch.health_records || []),
          {
            date: new Date().toISOString(),
            status: healthStatus,
            notes: healthNotes,
            recorded_by: 'current_user'
          }
        ]
      };
      
      await updateBatch(updatedBatch);
      setHealthDialogOpen(false);
      setHealthStatus('');
      setHealthNotes('');
      setSelectedBatch(null);
    } catch (error) {
      console.error('Failed to update health status:', error);
    }
  };

  const handleAddTreatment = async () => {
    if (!selectedBatch || !treatmentType) return;

    try {
      const treatmentData = {
        batch_id: selectedBatch.id,
        treatment_type: treatmentType,
        notes: treatmentNotes,
        date: treatmentDate,
        administered_by: 'current_user'
      };
      
      await addTreatment(treatmentData);
      setTreatmentDialogOpen(false);
      setTreatmentType('');
      setTreatmentNotes('');
      setTreatmentDate(new Date().toISOString().split('T')[0]);
      setSelectedBatch(null);
    } catch (error) {
      console.error('Failed to add treatment:', error);
    }
  };

  // Expenditure management functions
  const handleExpenditureChange = (id, field, value) => {
    setExpenditures(prev => 
      prev.map(exp => {
        if (exp.id === id) {
          const updated = { ...exp, [field]: value };
          
          // Auto-calculate amount when quantity or unit_price changes
          if (field === 'quantity' || field === 'unit_price') {
            const quantity = parseFloat((field === 'quantity' ? value : updated.quantity).toString().replace(/,/g, '')) || 0;
            const unitPrice = parseFloat((field === 'unit_price' ? value : updated.unit_price).toString().replace(/,/g, '')) || 0;
            const calculatedAmount = quantity * unitPrice;
            updated.amount = calculatedAmount > 0 ? formatCurrencyInput(calculatedAmount.toString()) : '';
          }
          
          return updated;
        }
        return exp;
      })
    );
  };

  const addExpenditure = () => {
    const newExp = {
      id: Date.now(),
      category: 'Food',
      type: '',
      quantity: '',
      unit_price: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0]
    };
    setExpenditures(prev => [...prev, newExp]);
  };

  const removeExpenditure = (id) => {
    if (expenditures.length > 1) {
      setExpenditures(prev => prev.filter(exp => exp.id !== id));
    }
  };

  const calculateTotalExpenditure = () => {
    return expenditures.reduce((total, exp) => {
      const amount = parseFloat(exp.amount.toString().replace(/,/g, '')) || 0;
      return total + amount;
    }, 0);
  };

  const handleSaveExpenditures = async () => {
    if (!selectedBatch) return;

    try {
      // Filter out expenditures with valid amounts
      const validExpenditures = expenditures.filter(exp => 
        parseFloat(exp.amount) > 0 && exp.category && exp.type
      );

      const updatedBatch = {
        ...selectedBatch,
        additional_expenditures: [
          ...(selectedBatch.additional_expenditures || []),
          ...validExpenditures.map(exp => ({
            ...exp,
            recorded_date: new Date().toISOString(),
            recorded_by: 'current_user'
          }))
        ]
      };
      
      await updateBatch(updatedBatch);
      setExpenditureDialogOpen(false);
      setExpenditures([
        { id: 1, category: 'Food', type: '', quantity: '', unit_price: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] }
      ]);
      setSelectedBatch(null);
    } catch (error) {
      console.error('Failed to save expenditures:', error);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Chicken Batch Management
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Batches
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalBatches}
                  </Typography>
                </Box>
                <Pets color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Batches
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.activeBatches}
                  </Typography>
                </Box>
                <Assessment color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Birds
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {stats.totalBirds.toLocaleString()}
                  </Typography>
                </Box>
                <TrendingUp color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg Mortality Rate
                  </Typography>
                  <Typography 
                    variant="h4" 
                    color={parseFloat(stats.avgMortalityRate) > 10 ? "error.main" : "warning.main"}
                  >
                    {stats.avgMortalityRate}%
                  </Typography>
                </Box>
                <TrendingDown 
                  color={parseFloat(stats.avgMortalityRate) > 10 ? "error" : "warning"} 
                  sx={{ fontSize: 40 }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Show alert if no data */}
      {!batches || batches.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No batch data available. Please check your internet connection or add new batches.
        </Alert>
      )}

      {/* Batch Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Batch Code</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Birds Count</TableCell>
                <TableCell align="center">Age (Weeks)</TableCell>
                <TableCell align="center">Mortality Rate</TableCell>
                <TableCell align="center">Health Status</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches && batches.map((batch) => {
                const mortalityRate = parseFloat(calculateMortalityRate(batch));
                
                return (
                  <TableRow key={batch.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {batch.batch_code || batch.code}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Created: {new Date(batch.created_at || batch.dateAdded).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={batch.type} 
                        variant="outlined" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1" fontWeight="bold">
                        {parseFloat(batch.current_count) || parseFloat(batch.count) || 0}
                      </Typography>
                      {batch.initial_count && batch.initial_count !== (batch.current_count || batch.count) && (
                        <Typography variant="caption" color="textSecondary">
                          Initial: {batch.initial_count}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {batch.age_in_weeks || batch.age || 'N/A'} weeks
                    </TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="body2" 
                        color={mortalityRate > 10 ? 'error' : 'textPrimary'}
                        fontWeight={mortalityRate > 10 ? 'bold' : 'normal'}
                      >
                        {mortalityRate}%
                      </Typography>
                      {mortalityRate > 10 && (
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(mortalityRate, 100)} 
                          color="error" 
                          sx={{ mt: 0.5, height: 4 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={batch.health_status || 'Good'} 
                        color={getHealthColor(batch.health_status)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={batch.status} 
                        color={getStatusColor(batch.status)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Tooltip title="View Batch Details">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => handleViewBatch(batch)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Record Mortality">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => {
                              setSelectedBatch(batch);
                              setMortalityDialogOpen(true);
                            }}
                          >
                            <Warning />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Count">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => {
                              setSelectedBatch(batch);
                              setNewCount(batch.current_count || batch.count);
                              setUpdateCountDialogOpen(true);
                            }}
                          >
                            <Update />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Health">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => {
                              setSelectedBatch(batch);
                              setHealthStatus(batch.health_status || 'Good');
                              setHealthDialogOpen(true);
                            }}
                          >
                            <HealthAndSafety />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add Treatment">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => {
                              setSelectedBatch(batch);
                              setTreatmentDialogOpen(true);
                            }}
                          >
                            <LocalHospital />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add Expenditure">
                          <IconButton 
                            size="small" 
                            color="warning"
                            onClick={() => {
                              setSelectedBatch(batch);
                              setExpenditures([
                                { id: 1, category: 'Food', type: '', quantity: '', unit_price: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] }
                              ]);
                              setExpenditureDialogOpen(true);
                            }}
                          >
                            <Receipt />
                          </IconButton>
                        </Tooltip>

                        {user?.role === 'admin' && (
                          <Tooltip title="Delete Batch (Admin only)">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteBatch(batch)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Record Mortality Dialog */}
      <Dialog open={mortalityDialogOpen} onClose={() => setMortalityDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Mortality - {selectedBatch?.batch_code || selectedBatch?.code}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Deaths"
            type="number"
            fullWidth
            variant="outlined"
            value={mortalityCount}
            onChange={(e) => setMortalityCount(e.target.value)}
            inputProps={{ min: 1, max: selectedBatch?.current_count || selectedBatch?.count }}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="outlined">
            <InputLabel>Reason for Mortality</InputLabel>
            <Select
              value={mortalityReason}
              onChange={(e) => setMortalityReason(e.target.value)}
              label="Reason for Mortality"
            >
              <MenuItem value="disease">Disease</MenuItem>
              <MenuItem value="accident">Accident</MenuItem>
              <MenuItem value="predator">Predator Attack</MenuItem>
              <MenuItem value="old_age">Old Age</MenuItem>
              <MenuItem value="weather">Weather Related</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMortalityDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRecordMortality} variant="contained" color="error">
            Record Mortality
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Batch Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete batch "{selectedBatch?.batch_code || selectedBatch?.code}"? 
            This action cannot be undone and will permanently remove all batch data including:
            <br />• Batch details and history
            <br />• Associated sales records
            <br />• Treatment records
            <br />• Expenditure records
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Delete Batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Count Dialog */}
      <Dialog open={updateCountDialogOpen} onClose={() => setUpdateCountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Bird Count - {selectedBatch?.batch_code || selectedBatch?.code}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Bird Count"
            type="number"
            fullWidth
            variant="outlined"
            value={newCount}
            onChange={(e) => setNewCount(e.target.value)}
            inputProps={{ min: 0 }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Current count: {selectedBatch?.current_count || selectedBatch?.count} birds
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateCountDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateCount} variant="contained">
            Update Count
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Batch Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete batch "{selectedBatch?.batch_code || selectedBatch?.code}"? 
            This action cannot be undone and will permanently remove all batch data including:
            <br />• Batch details and history
            <br />• Associated sales records
            <br />• Treatment records
            <br />• Expenditure records
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Delete Batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Health Status Dialog */}
      <Dialog open={healthDialogOpen} onClose={() => setHealthDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Health Status - {selectedBatch?.batch_code || selectedBatch?.code}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Health Status</InputLabel>
            <Select
              value={healthStatus}
              onChange={(e) => setHealthStatus(e.target.value)}
              label="Health Status"
            >
              <MenuItem value="Excellent">Excellent</MenuItem>
              <MenuItem value="Good">Good</MenuItem>
              <MenuItem value="Fair">Fair</MenuItem>
              <MenuItem value="Poor">Poor</MenuItem>
              <MenuItem value="Critical">Critical</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Health Notes"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={healthNotes}
            onChange={(e) => setHealthNotes(e.target.value)}
            placeholder="Add any observations or notes about the batch health..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHealthDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateHealth} variant="contained" color="success">
            Update Health Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Batch Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete batch "{selectedBatch?.batch_code || selectedBatch?.code}"? 
            This action cannot be undone and will permanently remove all batch data including:
            <br />• Batch details and history
            <br />• Associated sales records
            <br />• Treatment records
            <br />• Expenditure records
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Delete Batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Treatment Dialog */}
      <Dialog open={treatmentDialogOpen} onClose={() => setTreatmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Treatment - {selectedBatch?.batch_code || selectedBatch?.code}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Treatment Type</InputLabel>
            <Select
              value={treatmentType}
              onChange={(e) => setTreatmentType(e.target.value)}
              label="Treatment Type"
            >
              <MenuItem value="vaccination">Vaccination</MenuItem>
              <MenuItem value="medication">Medication</MenuItem>
              <MenuItem value="antibiotic">Antibiotic</MenuItem>
              <MenuItem value="vitamin">Vitamin Supplement</MenuItem>
              <MenuItem value="deworming">Deworming</MenuItem>
              <MenuItem value="preventive">Preventive Treatment</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Treatment Date"
            type="date"
            fullWidth
            variant="outlined"
            value={treatmentDate}
            onChange={(e) => setTreatmentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Treatment Notes"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={treatmentNotes}
            onChange={(e) => setTreatmentNotes(e.target.value)}
            placeholder="Medicine name, dosage, reason for treatment..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTreatmentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddTreatment} variant="contained" color="info">
            Add Treatment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Batch Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete batch "{selectedBatch?.batch_code || selectedBatch?.code}"? 
            This action cannot be undone and will permanently remove all batch data including:
            <br />• Batch details and history
            <br />• Associated sales records
            <br />• Treatment records
            <br />• Expenditure records
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Delete Batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expenditure Dialog */}
      <Dialog 
        open={expenditureDialogOpen} 
        onClose={() => setExpenditureDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt color="warning" />
            <Typography variant="h6">
              Add Expenditure - {selectedBatch?.batch_code || selectedBatch?.code}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="warning" />
                Batch Expenditures
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<Add />}
                onClick={addExpenditure}
              >
                Add Item
              </Button>
            </Box>
            
            {expenditures.map((expenditure) => (
              <Card key={expenditure.id} sx={{ mb: 2, backgroundColor: '#fff3e0' }}>
                <CardContent>
                  <Grid container spacing={1} alignItems="center">
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={expenditure.category}
                          onChange={(e) => handleExpenditureChange(expenditure.id, 'category', e.target.value)}
                          label="Category"
                        >
                          <MenuItem value="Food">🌾 Food/Feed</MenuItem>
                          <MenuItem value="Medicine">💊 Medicine</MenuItem>
                          <MenuItem value="Equipment">🔧 Equipment</MenuItem>
                          <MenuItem value="Transportation">🚚 Transport</MenuItem>
                          <MenuItem value="Labor">👷 Labor</MenuItem>
                          <MenuItem value="Maintenance">🔧 Maintenance</MenuItem>
                          <MenuItem value="Utilities">⚡ Utilities</MenuItem>
                          <MenuItem value="Other">📦 Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <TextField
                        size="small"
                        label={expenditure.category === 'Food' ? 'Feed Type' : 
                               expenditure.category === 'Medicine' ? 'Medicine Type' : 'Item Type'}
                        value={expenditure.type}
                        onChange={(e) => handleExpenditureChange(expenditure.id, 'type', e.target.value)}
                        fullWidth
                        placeholder={
                          expenditure.category === 'Food' ? 'Starter, Grower...' :
                          expenditure.category === 'Medicine' ? 'Vaccine, Drug...' :
                          'Specify item'
                        }
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <TextField
                        size="small"
                        label="Date"
                        type="date"
                        value={expenditure.date}
                        onChange={(e) => handleExpenditureChange(expenditure.id, 'date', e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 1.5 }}>
                      <TextField
                        size="small"
                        label="Quantity"
                        type="number"
                        value={expenditure.quantity}
                        onChange={(e) => handleExpenditureChange(expenditure.id, 'quantity', e.target.value)}
                        fullWidth
                        placeholder="1"
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 1.5 }}>
                      <TextField
                        size="small"
                        label="Unit Price"
                        value={expenditure.unit_price}
                        onChange={(e) => {
                          const formattedValue = formatCurrencyInput(e.target.value);
                          handleExpenditureChange(expenditure.id, 'unit_price', formattedValue);
                        }}
                        fullWidth
                        placeholder="0"
                        helperText="TZS"
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 1.5 }}>
                      <TextField
                        size="small"
                        label="Total"
                        value={expenditure.amount}
                        fullWidth
                        disabled
                        placeholder="0"
                        helperText="Calculated"
                        sx={{ 
                          '& .MuiInputBase-input.Mui-disabled': { 
                            WebkitTextFillColor: '#f57c00',
                            fontWeight: 'bold'
                          } 
                        }}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <TextField
                        size="small"
                        label="Description"
                        value={expenditure.description}
                        onChange={(e) => handleExpenditureChange(expenditure.id, 'description', e.target.value)}
                        fullWidth
                        placeholder="Optional notes"
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 0.5 }}>
                      <IconButton 
                        color="error" 
                        onClick={() => removeExpenditure(expenditure.id)}
                        disabled={expenditures.length === 1}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
            
            {/* Total Expenditure Display */}
            <Card sx={{ backgroundColor: '#fff3e0', border: '2px solid #f57c00' }}>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#f57c00' }}>
                  <span>Total Expenditure:</span>
                  <span>{formatCurrency(calculateTotalExpenditure())}</span>
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpenditureDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveExpenditures} variant="contained" color="warning">
            Save Expenditures
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Batch Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete batch "{selectedBatch?.batch_code || selectedBatch?.code}"? 
            This action cannot be undone and will permanently remove all batch data including:
            <br />• Batch details and history
            <br />• Associated sales records
            <br />• Treatment records
            <br />• Expenditure records
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Delete Batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Batch Dialog */}
      {selectedBatch && (
        <ViewBatch
          open={viewBatchOpen}
          onClose={() => setViewBatchOpen(false)}
          batch={selectedBatch}
          onEdit={() => {
            setViewBatchOpen(false);
            // Add edit functionality if needed
          }}
          onSell={() => {
            setViewBatchOpen(false);
            // Add sell functionality if needed
          }}
          onExpenditure={() => {
            setViewBatchOpen(false);
            setExpenditures([
              { id: 1, category: 'Food', type: '', quantity: '', unit_price: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] }
            ]);
            setExpenditureDialogOpen(true);
          }}
        />
      )}
    </Box>
  );
};

export default ChickenBatchManagement;
